import { useState } from 'react';
import QRCode from 'qrcode';
import { AlertTriangle, Check, Copy, Download, QrCode } from 'lucide-react';
import { ProvisioningTabs } from '../components/ProvisioningTabs';
import { api } from '../utils/api';
import type { DeviceRole, LifecycleStatus } from '../types';
import './ProvisionGenerate.css';

interface FactoryBundle {
  deviceUuid: string;
  serialNumber: string;
  nodeId: number;
  role: DeviceRole;
  lifecycleStatus: LifecycleStatus;
  hmacKey: string;
  deviceSecret: string;
  bakeCommand: string[];
  bakeCommandSingleLine: string;
  bakeUsesMultiCommand: boolean;
  qrUrl: string;
  createdAt: string;
}

const ROLE_HELP: Record<DeviceRole, string> = {
  leaf: 'LoRa-only sensor node. It needs a same-site gateway when adopted.',
  cellular: 'Standalone cellular node with its own cloud uplink.',
  gateway: 'Cellular bridge-gateway that relays leaf nodes.',
};

export function ProvisionGenerate() {
  const [role, setRole] = useState<DeviceRole>('leaf');
  const [serialNumber, setSerialNumber] = useState('');
  const [bundle, setBundle] = useState<FactoryBundle | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const generate = async () => {
    setBusy(true);
    setError('');
    setCopied(false);
    setBundle(null);
    try {
      const response: FactoryBundle = await api.post('/api/v1/provisioning', {
        role,
        ...(serialNumber.trim() ? { serialNumber: serialNumber.trim() } : {}),
      });
      setBundle(response);
      setQrDataUrl(await QRCode.toDataURL(response.qrUrl, { width: 320, margin: 2 }));
    } catch (generateError) {
      setError(generateError instanceof Error ? generateError.message : 'Failed to generate device');
    } finally {
      setBusy(false);
    }
  };

  const copyBakeCommand = async () => {
    if (!bundle) return;
    await navigator.clipboard.writeText(bundle.bakeCommand.join('\n'));
    setCopied(true);
  };

  const downloadBundle = () => {
    if (!bundle) return;
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = `${bundle.serialNumber}-factory-bundle.json`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  };

  return (
    <div className="provision-page">
      <ProvisioningTabs />
      <div className="provision-header">
        <QrCode size={28} />
        <div><h1>Factory Provision</h1><p>Mint the permanent identity, QR label, and complete firmware bake command.</p></div>
      </div>

      <div className="provision-grid">
        <section className="provision-card">
          <h2>1. Mint device</h2>
          <label className="provision-field"><span>Device role</span>
            <select value={role} onChange={(event) => setRole(event.target.value as DeviceRole)} disabled={busy}>
              <option value="leaf">Leaf (LoRa sensor)</option>
              <option value="cellular">Cellular (standalone)</option>
              <option value="gateway">Gateway (cellular bridge)</option>
            </select>
            <small>{ROLE_HELP[role]}</small>
          </label>
          <label className="provision-field"><span>Serial number <em>(optional)</em></span>
            <input value={serialNumber} onChange={(event) => setSerialNumber(event.target.value)} placeholder="Auto-generate RTM-…" disabled={busy} />
          </label>
          <button className="provision-btn" type="button" onClick={generate} disabled={busy}>{busy ? 'Generating…' : 'Generate factory bundle'}</button>
          {error && <p className="provision-error">{error}</p>}
        </section>

        {bundle && (
          <section className="provision-card">
            <h2>2. Bake and label</h2>
            <div className="provision-warning"><AlertTriangle size={18} /><span>These secrets are shown <strong>once</strong>. Copy or save this bundle now; they cannot be retrieved later.</span></div>
            <div className="provision-qr">
              {qrDataUrl && <img src={qrDataUrl} alt={`Permanent QR code for ${bundle.serialNumber}`} />}
              <strong>{bundle.serialNumber}</strong>
              <p className="provision-url">{bundle.qrUrl}</p>
              <small>Print this permanent QR on the device label.</small>
            </div>
            <div className="provision-command">
              <div className="provision-command__header"><strong>Complete C6 bake command</strong><span>{bundle.bakeUsesMultiCommand ? '2 lines' : '1 line'}</span></div>
              <pre>{bundle.bakeCommand.join('\n')}</pre>
              <button className="provision-btn provision-btn--secondary" type="button" onClick={copyBakeCommand}>
                {copied ? <Check size={16} /> : <Copy size={16} />}{copied ? 'Copied' : 'Copy complete command'}
              </button>
            </div>
            <dl className="provision-keys">
              <div><dt>device_uuid</dt><dd className="mono">{bundle.deviceUuid}</dd></div>
              <div><dt>node_id / role</dt><dd>{bundle.nodeId} / {bundle.role}</dd></div>
              <div><dt>hmac_key</dt><dd className="mono">{bundle.hmacKey}</dd></div>
              <div><dt>device_secret</dt><dd className="mono">{bundle.deviceSecret}</dd></div>
            </dl>
            <button className="provision-btn provision-btn--secondary" type="button" onClick={downloadBundle}><Download size={16} />Save one-time bundle</button>
          </section>
        )}
      </div>
    </div>
  );
}

export default ProvisionGenerate;
